import { Pipe, PipeTransform, inject } from '@angular/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { parseValue } from 'apps/account/src/account-shared/pipes/parse-value.util';
import dayjs from 'dayjs/esm/index';

/**
 * Local standalone pipes for the native story modal (FtStoryModalComponent), byte-faithful
 * to the AngularJS filters the FROZEN baseline `storyModal.html` binds. Kept LOCAL + standalone
 * (same self-containment rule the curation-modal / contest-sheets pipes follow) so the story
 * modal never routes through the eval-order-fragile account-shared barrel
 * ([[project_account_barrel_eval_order_fragility]]). The date pipes call `dayjs`
 * directly; `parse` is the native `parseValue`
 * util (WS2 #31), `ftAzureStoredPhoto` the native `buildAzureStoredPhotoUrl` util (WS2 #29), and
 * `| translate` the native ngx `TranslateModule` (WS2 translate sweep drained `StoryTranslatePipe`).
 */

/** Native port of v1 `$filter('parse')` (e.g. `value | parse:'number'`) via parseValue. */
@Pipe({ name: 'parse' })
export class StoryParsePipe implements PipeTransform {
  transform(value: any, type?: string): any {
    return parseValue(value, type);
  }
}

/** Native port of v1 `$filter('ftAzureStoredPhoto')` (`photo | ftAzureStoredPhoto[:size]`). */
@Pipe({ name: 'ftAzureStoredPhoto' })
export class StoryAzurePhotoPipe implements PipeTransform {
  private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);
  transform(photo: any, size?: string): string | null {
    return buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl);
  }
}

/**
 * v1 `daysSince` (account-v1/.../date-range/daysUntilDate.filter.js): Today / Yesterday /
 * "N days ago" (≤14) / "MMM D, YYYY" otherwise. Uses `dayjs` so the
 * day-diff + format match v1 character-for-character. (Old dates → stable formatted string,
 * independent of "now" → deterministic across captures.)
 */
@Pipe({ name: 'daysSince' })
export class StoryDaysSincePipe implements PipeTransform {
  transform(start: any): string {
    const startDate = start ? dayjs(start) : dayjs();
    const daysSince = dayjs().diff(startDate, 'days');
    if (daysSince < 1) return 'Today';
    if (daysSince === 1) return 'Yesterday';
    if (daysSince <= 14) return daysSince + ' days ago';
    return dayjs(start).format('MMM D, YYYY');
  }
}

/**
 * v1 `daysUntilDate` (same file): `dayjs(end).diff(start||now minus 1 day, 'days')`.
 * Used by the recommendation notification (inert at the gate — recommendation is null).
 */
@Pipe({ name: 'daysUntilDate' })
export class StoryDaysUntilDatePipe implements PipeTransform {
  transform(end: any, start?: any): number {
    const startDate = start ? dayjs(start) : dayjs();
    return dayjs(end).diff(startDate.subtract(1, 'days'), 'days');
  }
}
